/**
 * Belge (e-bilet) HTML yardımcıları.
 *
 * `Document/Eticket` sunucudan HTML döner (PDF değil) ve fiyat/ücret bilgisini her zaman
 * içerir. "Fiyatsız" e-bilet için backend'de bir bayrak yok (`docs/backend-requirements.md`
 * N-11) — o güne dek fiyat sütunları/satırları istemci tarafında ayıklanır.
 */

const PRICE_HEADER_RE = /(tutar|fiyat|ücret|ucret|price|amount|toplam)/i;
const PRICE_LINE_RE = /(grand\s*total|toplam\s*fiyat|toplam\s*tutar)/i;
const CURRENCY_RE = /\d[\d.,]*\s*(TRY|TL|EUR|USD|₺|€|\$)/i;

/** `open()` ile yeni sekmede açılan belge HTML'ini normalize eder — şimdilik yalnız
 *  fiyatsız dönüşüm. Parse edilemezse orijinali döndürür. */
export function stripDocumentPrices(html: string): string {
  if (typeof DOMParser === 'undefined') return html;
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch {
    return html;
  }
  if (!doc?.body) return html;

  doc.querySelectorAll('table').forEach((table) => {
    const headerRow =
      table.querySelector('thead tr') ?? table.querySelector('tr');
    if (!headerRow) return;
    const headerCells = Array.from(headerRow.children);
    const priceCols = headerCells
      .map((cell, i) => (PRICE_HEADER_RE.test(cell.textContent ?? '') ? i : -1))
      .filter((i) => i >= 0);
    if (priceCols.length === 0) return;
    const drop = new Set(priceCols);
    table.querySelectorAll('tr').forEach((row) => {
      Array.from(row.children).forEach((cell, i) => {
        if (drop.has(i)) cell.remove();
      });
    });
  });

  // Tabloların dışındaki "Toplam Fiyat / Grand Total" gibi tek satırlık toplam blokları.
  doc.querySelectorAll('p, div, h1, h2, h3, h4, h5, li, span').forEach((el) => {
    const text = el.textContent ?? '';
    if (PRICE_LINE_RE.test(text) || (CURRENCY_RE.test(text) && text.trim().length < 60)) {
      if (!el.querySelector('table')) el.remove();
    }
  });

  return `<!doctype html>${doc.documentElement.outerHTML}`;
}

/**
 * Open server-rendered document HTML in a fresh tab; optionally auto-trigger the print
 * dialog. Shared by `DocumentMenu` (e-ticket / voucher) and `AirResultsList` ("Teklif
 * Oluştur").
 *
 * NOTE: no `noopener` in the features string — that makes `window.open` return `null`,
 * and we need the handle to write our own (trusted) document into it (docs/STATUS.md A10).
 */
export function openHtmlDocument(html: string, print: boolean): void {
  const win = window.open('', '_blank');
  if (!win) throw new Error('Açılır pencere engellendi — tarayıcı ayarlarını kontrol edin.');
  win.document.open();
  win.document.write(html);
  win.document.close();
  if (print) {
    // `document.write` sonrası `load` olayı güvenilir tetiklenmiyor — hem onload'a
    // hem de kısa bir gecikmeye bağlan (hangisi önce olursa).
    let printed = false;
    const doPrint = () => {
      if (printed || win.closed) return;
      printed = true;
      win.focus();
      win.print();
    };
    win.onload = doPrint;
    win.setTimeout(doPrint, 600);
  }
}
